/**
 * Reports errors the app throws to the database (`report_client_error`, supabase/update-5-admin-health.sql)
 * so the admin can list them. Quiet by design: at most 20 per visit, the same error once, text cut to
 * size, and it never throws or reports on a failure to report.
 */
export const REPORT_LIMIT = 20;
export const MESSAGE_MAX = 1024;
export const STACK_MAX = 4096;

export interface ErrorReport { message: string; stack?: string; route?: string }

export interface ErrorReporterDeps {
  /** Sends one report. A rejection is ignored. */
  send: (r: Required<Pick<ErrorReport, "message">> & ErrorReport) => Promise<unknown>;
  /** False while nobody is signed in or no backend is set up. */
  enabled: () => boolean;
}

/** Plain text of whatever was thrown (an Error, a string, anything). */
export function describeError(e: unknown): { message: string; stack?: string } {
  if (e instanceof Error) return { message: `${e.name}: ${e.message}`, stack: e.stack };
  if (typeof e === "string") return { message: e };
  try { return { message: JSON.stringify(e) ?? String(e) }; } catch { return { message: String(e) }; }
}

export function createErrorReporter({ send, enabled }: ErrorReporterDeps) {
  const seen = new Set<string>();
  let sent = 0;
  return function report(e: unknown, route?: string): boolean {
    if (!enabled() || sent >= REPORT_LIMIT) return false;
    const d = describeError(e);
    const message = d.message.slice(0, MESSAGE_MAX).trim();
    if (!message || seen.has(message)) return false;
    seen.add(message);
    sent++;
    send({ message, stack: d.stack?.slice(0, STACK_MAX), route }).catch(() => undefined);
    return true;
  };
}
